<script lang="ts">
import {Component, Watch, toNative} from 'vue-facing-decorator'
import {asArray, asElement, objectOf} from '../../../utils/arrayUtils'
import {fieldVisible} from '@/types/Idea'
import Instance from "@/types/Instance";
import Idea from "@/types/Idea";
import IdeaProperty from "@/types/IdeaProperty";
import OntosInstanceRelInput from "@/components/live/instance/InstanceRelInput.vue";
import OntosInstanceStateInput from "@/components/live/instance/InstanceStateInput.vue";
import {EnhancedWorkflow} from "@/types/Workflow";
import IdeaLevelRouteComponent from "@/components/shared/IdeaLevelRouteComponent";
import {knownForCreate, lastCreated, offerKnown, requestCreation} from "@/utils/instanceCreation";
import {collectionReadConfig, handOverCollection, takeCollectionRead} from "@/utils/collectionRead";
import {Preview, previewOfOpened} from "@/utils/instancePreview";
import ViewInstanceDialog from "@/components/live/instance/ViewInstanceDialog.vue";
import {InstanceContext} from "@/types/InstanceContext";
import InstanceInput from "@/components/live/instance/InstanceInput.vue";
import ColoredOptionChip from "@/components/shared/ColoredOptionChip.vue";
import {ColoredOption} from "@/types/ColoredOption";
import PropertyValue from "@/components/live/instance/PropertyValue.vue";
import ImagePreviewDialog from "@/components/shared/ImagePreviewDialog.vue";
import CheckMenu, {CheckMenuItem} from "@/components/shared/CheckMenu.vue";
import CollectionViewTabs from "@/components/live/instance/CollectionViewTabs.vue";
import {ImagePreview} from "@/types/ImagePreview";
import {isMediaType} from "@/utils/mediaTypes";
import ChoiceInput from "@/components/live/instance/ChoiceInput.vue";
import {domainOfProperty} from "@/types/Domain";
import {hiddenCount, instanceColorOf, isChoiceField, needsValueSeparator, relatedValues, valueText} from "@/utils/propertyDisplay";
import HiddenCount from "@/components/shared/HiddenCount.vue";
import PersonSelect from "@/components/shared/PersonSelect.vue";
import {hasCompositeIdentifier} from "@/utils/ontosUtils";
import {failuresAt} from "@/utils/fieldValidation";
import {failureText} from "@/utils/saveErrors";
import {orderedProperties} from "@/utils/instanceSections";
import {markOrigin} from "@/utils/motion";
import InstanceIdentifier from "@/components/shared/InstanceIdentifier.vue";
import ColorAccent from "@/components/shared/ColorAccent.vue";
import InstanceListRow, {RowField, rowFieldsOf} from "@/components/live/instance/InstanceListRow.vue";
import TableFit from "@/components/shared/TableFit.vue";
import ShowClosedToggle from "@/components/shared/ShowClosedToggle.vue";

type Header = {
  title: string,
  key: string,
  width?: string,
  align?: string
  sortable?: boolean
  slot?: string
  cellProps?: { class: string }
  filter?: object
  //Compara as INSTÂNCIAS, não o valor da célula: é o que dá acesso ao _rels
  sortRaw?: (a: Instance, b: Instance) => number
}

require('clickout-event')
//import { alignment, defaultDataType } from 'export-xlsx';
const ExcelExport = require('export-xlsx') as any
@Component({
  components: {
    OntosInstanceRelInput,
    OntosInstanceStateInput,
    'ontos-choice-input': ChoiceInput,
    'ontos-person-select': PersonSelect,
    InstanceInput,
    'ontos-property-value': PropertyValue,
    'ontos-hidden-count': HiddenCount,
    'ontos-view-instance-dialog': ViewInstanceDialog,
    'ontos-image-preview-dialog': ImagePreviewDialog,
    'ontos-collection-view-tabs': CollectionViewTabs,
    'ontos-check-menu': CheckMenu,
    'ontos-colored-option': ColoredOptionChip,
    'ontos-color-accent': ColorAccent,
    'ontos-instance-identifier': InstanceIdentifier,
    'ontos-instance-list-row': InstanceListRow,
    'ontos-table-fit': TableFit,
    'ontos-show-closed-toggle': ShowClosedToggle
  }
})
class ListInstances extends IdeaLevelRouteComponent {
  //exposto ao template: o predicado é função de módulo, não é enxergado de lá
  isMediaType = isMediaType
  //O mesmo nome da função, para o template chamar sem um método de repasse
  needsValueSeparator = needsValueSeparator
  hiddenCount = hiddenCount
  //idem: a cor de um item é função de módulo, e a régua da linha a chama do template
  instanceColorOf = instanceColorOf
  //idem: o predicado da chave composta decide a coluna do identificador e a célula do filtro
  hasCompositeIdentifier = hasCompositeIdentifier


  showFilter: boolean = false;
  isShowClosedItems: boolean = false; //manter public para reagir
  /**
   * A ordem da grade. Era só da tabela, mudada pelo clique no cabeçalho; ficou aqui porque no
   * visão compacta o cabeçalho some, e quem ordena é o menu "Ordenar" — os dois mudam a mesma.
   */
  sortBy: Array<{ key: string, order?: 'asc' | 'desc' }> = []

  /** As colunas por que se pode ordenar, para o menu da visão compacta: as mesmas do cabeçalho. */
  get sortableHeaders(): Header[] {
    return this.headers.filter(header => header.key !== '_filter' && header.sortable !== false)
  }

  /** A coluna da ordem atual e a direção, para o rótulo do menu; nada quando é a ordem de chegada. */
  get currentSort(): { title: string, order: 'asc' | 'desc' } | null {
    const [sort] = this.sortBy
    const header = sort && this.sortableHeaders.find(candidate => candidate.key === sort.key)
    return header ? {title: header.title as string, order: sort.order || 'asc'} : null
  }

  /** A mesma coluna inverte a direção; outra começa crescente — o que o clique no cabeçalho faz. */
  sortOn(key: string) {
    const [sort] = this.sortBy
    this.sortBy = [{key, order: sort && sort.key === key && sort.order !== 'desc' ? 'desc' : 'asc'}]
  }

  /** O que vai embaixo do nome do item, na visão compacta — ver `rowFieldsOf`. */
  rowFields(item: Instance): RowField[] {
    return rowFieldsOf(this.visibleProperties.filter(property => fieldVisible(item, property)),
        property => this.propertyItems(property, item))
  }
  instanceOnView: InstanceContext | null = null;
  imagePreview: ImagePreview | null = null;

  /** O que a grade já tem do item aberto, para a ficha mostrar já — ver `previewOfOpened`. */
  previewOnView: Preview | null = null

  //Abre modal em vez de navegar, sem mexer na URL
  openInstance(context: InstanceContext) {
    this.instanceOnView = context
    this.previewOnView = previewOfOpened(context, this.instances, this.idea)
  }

  /** O clique na linha não passa pelo `onInstanceLink`, e anota a origem por conta própria. */
  openRow(event: MouseEvent, item: Instance) {
    markOrigin(event.currentTarget)
    this.openInstance({...this.$route.params, instance: item._instance} as InstanceContext)
  }

  /**
   * O que a janela precisa para abrir já criando.
   *
   * Esta tela lê com `rel-domains` para desenhar as colunas e os filtros de relacionamento, e a
   * coleção com fluxo e domínios sai do ambiente — então o formulário abre sem requisição. A
   * montagem é a MESMA do quadro, e por isso mora no `knownForCreate`.
   */
  get creating(): any {
    return {known: knownForCreate(this.workspace, this.params.idea, this.relDomains, this.reverseInfo)}
  }

  //exposta ao template: função de módulo não é enxergada de lá
  requestCreation = requestCreation

  /**
   * A janela de criar mora no casco, e não aqui (ver o `requestCreation`): é por este aviso que a
   * lista sabe que nasceu um item dela — criado pelo "Criar" daqui, pelo da faixa ou pelo "+" da
   * barra — e se relê para mostrá-lo.
   */
  get createdNotice() {
    return lastCreated.value
  }

  @Watch('createdNotice')
  onCreatedNotice(notice: { idea: string } | null) {
    if (notice && notice.idea === this.params.idea) this.load()
  }

  texto = ""


  @Watch('isShowClosedItems')
  onIsShowClosedItemsChanged(value: boolean) {
    if (this.workflow) {
      const workflow = this.workflow!
      if (value) {
        this.filters.rel._state = []
      } else if (this.instances.some(instance => workflow.isFinal(instance))) {
        this.filters.rel._state = workflow.nonFinalStates.map(state => state.id)
      }
    }
  }

  enableFilter() {
    this.showFilter = true;
  }

  /** A rota em que esta listagem montou — ver o `from` do `handOverCollection`. */
  private arrivedAt: string | null = null

  /**
   * Os itens da coleção — entregues pelo quadro, ou lidos.
   *
   * A MESMA leitura que o quadro faz (ver `collectionRead`), e é o que permite alternar
   * Lista ↔ Kanban sem ir ao servidor. Quem chega por URL, F5 ou voltar não encontra entrega e
   * lê, como sempre.
   */
  async load() {
    this.arrivedAt = (this.$route?.name as string) || null
    const {system: systemAlias, idea: ideaAlias} = this.params
    const res = takeCollectionRead(this.context) || await this.$instances.list(
        {systemAlias, ideaAlias}, collectionReadConfig(true))
    this.read = res
    this.instances = res.value
    this.ideas = this.workspace.ideas
    this.relDomains = res.relDomains
    this.reverseInfo = res.reverseInfo

    //this.loadedComputedProperties = true
    //await this.loadRels()
    this.loadColumns()

    //Preenche os states default
    if (this.workflow) {
      this.instances.filter(instance => !instance._state)
          .forEach(instance => instance._state = this.workflow!.initialState.id)
    }

    //Preenche as propriedades reversas — e os relacionamentos calculados, pelo mesmo motivo: nenhum
    //dos dois tem a lista de ids gravada no item, só o `_rels` da leitura, e é pelos ids que o
    //filtro da coluna compara. Sem isto, escolher um valor no filtro escondia tudo.
    const reversePropertyAliases = this.idea.properties
        .filter(property => property.relationship?.reverse || (property.relationship && property.formula))
        .map(property => property.alias);
    this.instances.forEach(instance => {
      reversePropertyAliases.forEach(prop => instance[prop] = asArray(instance._rels[prop]).map(relValue => relValue._instance))
    })
    this.loadFilters()

    this.onIsShowClosedItemsChanged(this.isShowClosedItems);
  }

  loadFilters() {
    this.idea.properties.forEach(property => {
      if (this.isRangeType(property)) {
        this.filters.minimum[property.alias] = ""
        this.filters.maximum[property.alias] = ""
      } else if (isChoiceField(property)) {
        //seleção e usuário filtram por lista de ids, como o relacionamento
        this.filters.rel[property.alias] = []
      } else {
        this.filters.text[property.alias] = ""
      }
    })
    this.filters.rel._state = []
  }

  objectOf = objectOf
  visibleColumns: Array<string> = []

  get columnMenuItems(): CheckMenuItem[] {
    return this.visibleColumnOptions.map(property => ({
      value: property.alias, title: property.name
    }))
  }

  /**
   * As colunas visíveis, lembradas por coleção — mesmo contrato dos campos do Kanban: o
   * null é "nunca escolhi", e cai em todas.
   *
   * Filtra a lista do DESIGN pelo que está guardado, e não o contrário: assim a ordem é
   * sempre a de declaração da coleção e aliases que sumiram se perdem sozinhos. Se isso
   * esvaziar, volta a todas — uma tabela sem coluna nenhuma não é escolha de ninguém.
   */
  loadColumns() {
    const {system: systemAlias, idea: ideaAlias} = this.params
    const all = this.visibleColumnOptions.map(property => property.alias)
    const stored = this.$storage.listColumns(systemAlias, ideaAlias)
    const kept = stored === null ? all : all.filter(alias => stored.includes(alias))
    this.visibleColumns = kept.length ? kept : all
  }

  onColumnsChange(aliases: string[]) {
    const {system: systemAlias, idea: ideaAlias} = this.params
    //A ordem de declaração já vem do menu, que ordena pelos seus itens
    this.visibleColumns = aliases
    this.$storage.setListColumns(systemAlias, ideaAlias, aliases)
  }
  filters: {
    minimum: { [k: string]: any },
    maximum: { [k: string]: any },
    text: { [k: string]: any }
    //ids de instância nos relacionamentos; aliases de situação em _state
    rel: { [k: string]: Array<number | string> }
  } = {
    minimum: {},
    maximum: {},
    text: {},
    rel: {}
  }
  instances: Instance[] = []
  ideas: Idea[] = []
  relDomains: any = null
  reverseInfo: any = null
  /**
   * A resposta do servidor como veio, para o quadro receber o que receberia dele.
   *
   * O `instances` não serve: o `load` apaga dele os valores das propriedades invisíveis e
   * preenche os reversos. Guardar a resposta é o que mantém a entrega igual a uma leitura.
   */
  private read: any = null

  /** O contexto da coleção, que é a chave da entrega entre as duas visões. */
  get context(): InstanceContext {
    const {system, idea} = this.params
    return {system, idea} as InstanceContext
  }

  /**
   * Entrega a leitura para a visão irmã, se for para lá que se está indo — ver
   * `handOverCollection`.
   */
  beforeUnmount() {
    this.stopOffering?.()
    handOverCollection(this.context, this.$route?.name as string, this.read, this.arrivedAt)
  }

  /**
   * O que esta lista leu, oferecido a quem criar na coleção dela — o "Criar" da faixa do caminho
   * mora no casco, e é por aqui que a janela abre sem ir ao servidor. Ver o `offerKnown`.
   */
  private stopOffering: (() => void) | null = null

  created() {
    this.stopOffering = offerKnown(this.params.idea, () => this.creating.known)
  }

  //Exposta ao template: quem vê cada campo é decidido por item, pelo servidor — o valor oculto nem
  //vem, e por isso também não se filtra nem ordena por ele
  fieldVisible = fieldVisible
  loadedRels = false



  /**
   * As colunas visíveis, na ordem em que a grade as mostra: as da CHAVE primeiro.
   *
   * Uma fonte só porque a linha de filtros monta uma célula por coluna e precisa da mesma
   * ordem — em ordens diferentes, cada filtro cai debaixo da coluna errada. O ordenador é o
   * mesmo `orderedProperties` que a ficha usa para os campos, e ele é estável dentro de cada
   * grupo: reordenar no design continua se refletindo aqui.
   */
  get visibleProperties(): IdeaProperty[] {
    return orderedProperties(this.idea.properties.filter(p => this.visibleColumns.includes(p.alias)))
  }

  get headers(): Array<Header> {
    if (!this.idea.properties.length) return []
    let headers: Array<Header> = this.visibleProperties
        .map(p => {
          return {
            title: p.name,
            key: p.alias,
            slot: 'header.' + p.alias,
            //O nome do item em destaque (ver .ontos-list-table): a coluna da chave, quando ela é
            //um campo só; a composta tem a coluna própria, logo abaixo
            ...(p.identifier && !hasCompositeIdentifier(this.idea) ? {cellProps: {class: 'ontos-list-name'}} : {}),
            //A miniatura tem tamanho fixo, mas sem isto a coluna ainda reserva
            //espaço pela largura natural do conteúdo. Ordenar por URL de imagem,
            //além de não significar nada, daria uma ordem que parece intencional.
            ...(isMediaType(p) ? {width: '1%', sortable: false} : {}),
            //Relacionamento ordena pelo NOME, não pelo id.
            //
            //Na célula a instância guarda o id (`genero: 1`) e o nome vem do _rels, que
            //é de onde o propValue o tira. Sem dizer nada, a tabela ordena pelo que está
            //em `item[key]` — o id —, e a ordem saía por data de criação disfarçada de
            //alfabética. Vinha de antes da migração: a versão em Vue 2 também não tinha
            //função de ordenação aqui.
            //
            //`sortRaw`, e não `sort`: só ele recebe a instância inteira, que é onde
            //mora o _rels. O texto é montado do mesmo jeito que a célula monta o dela,
            //para a ordem bater com o que se lê.
            ...(p.relationship ? {sortRaw: this.relationshipSort(p.alias)} : {}),
            //Seleção ordena pela POSIÇÃO da opção no domínio — nem pelo id, que é ordem
            //de criação, nem pelo nome —, a mesma decisão da coluna de situação.
            ...(p.type === 'choice' ? {sortRaw: this.choiceSort(p)} : {}),
            //Usuário ordena pelo NOME da pessoa, como a célula a mostra — não pelo id da conta
            ...(p.type === 'user' ? {sortRaw: this.userSort(p)} : {}),
            filter: (value: any) => {
              let f = this.filters.text[p.alias]
              if (f) {
                let property = objectOf(this.idea.properties, 'alias')[p.alias]
                let elements = property.relationship ? asArray(value).map(v => v._identifier) : asArray(value)
                return elements.some(v => (v + '').toLowerCase().includes((f + '').toLowerCase()))
              }

              const min = this.filters.minimum[p.alias]
              const max = this.filters.maximum[p.alias]
              if (min && max) return value >= min && value <= max
              else if (min) return value >= min
              else if (max) return value <= max

              f = this.filters.rel[p.alias]
              if (f && f.length) {
                return asArray(value).some((v: any) => f.includes(v))
              }
              return true
            }
          }
        })

    //Coluna sem conteúdo no corpo: existe só para o botão de filtro do cabeçalho.
    //O key é _filter, e não _identifier, de propósito: a instância não tem esse
    //campo, então o corpo renderiza vazio sem precisar de slot. Com _identifier, o
    //v-data-table cairia no padrão e imprimiria o identificador.
    //Tem de continuar em índice 0 — o focusFilter do cabeçalho usa headers[1].
    //
    //O filtro que sempre aprova NÃO é enfeite. O Vuetify 4 combina os filtros de coluna
    //no modo `intersection`, e sem busca digitada um item só passa se TODA coluna tiver
    //filtro: sem este, a lista sairia sempre vazia.
    headers.unshift({
      title: '',
      key: '_filter',
      width: '1%',
      sortable: false,
      filter: () => true
    })
    //O identificador logo depois do acento, e só quando a chave é COMPOSTA: com um campo só
    //ele sairia idêntico à coluna vizinha. O `filter` que sempre aprova não é enfeite — ver o
    //comentário da coluna acima: sem ele, esta coluna zeraria a listagem inteira.
    if (hasCompositeIdentifier(this.idea)) {
      headers.splice(1, 0, {
        title: this.idea.name,
        key: '_identifier',
        //'header.…' porque neste componente `slot` nomeia o slot do CABEÇALHO: há um laço
        //genérico que registra #[h.slot] e desenha o título da coluna. Com um nome de célula
        //aqui, esse laço passava a desenhar o título dentro de toda linha.
        slot: 'header._identifier',
        cellProps: {class: 'ontos-list-name'},
        filter: () => true
      } as Header)
    }

    headers[0].align = 'start'

    if (this.workflow) {
      // const filter = (values: any) => asArray(values).find(value =>
      //     !asArray(this.filters.rel._state).length ||
      //     asArray(this.filters.rel._state).find(filter => filter === value))
      const filter = (value: number) => {
        const chosen = asArray(this.filters.rel._state)
        return !chosen.length || chosen.includes(value)
      }

      //Ordena pela POSIÇÃO no fluxo, e não pelo id — ver o stateSort
      headers.push({
        title: this.$tt('state') as string, key: '_state', filter, slot: 'header._state',
        sortRaw: this.stateSort
      })
    }

    /*
       headers.push({
         textModel: 'Actions',
         value: '_actions',
         slot: 'header._actions',
         align: 'right',
         width: '1%',
         sortable: false
       })
     }
     */
    return headers
  }

  /** As colunas que se pode mostrar: a do campo que quem olha não vê em item nenhum some. */
  get visibleColumnOptions() {
    if (!this.instances.length) return this.idea.properties
    return this.idea.properties.filter(property => this.instances.some(instance => fieldVisible(instance, property)))
  }

  get itemsPerPageOptions() {
    let items = []
    for (let currentDecene = 10; ; currentDecene *= 10) {
      let v = currentDecene
      let blockSize = this.instances.length / 4;
      if (v <= blockSize) {
        items.push(v)
      } else break
      v = currentDecene * 2
      if (v <= blockSize) {
        items.push(v)
      } else break
      v = currentDecene * 5
      if (v <= blockSize) {
        items.push(v)
      } else break
    }
    if (this.instances.length >= 20) {
      items.push(Math.ceil(this.instances.length / 2))
    }
    items.push(this.instances.length)
    return items
  }

  get itemsPerPage() {
    return Math.ceil(this.instances.length < 100 ? this.instances.length : this.instances.length <= 200 ? this.instances.length / 2 : 100)
  }

  get excelSettings() {
    return {
      // Table settings
      fileName: this.idea.pluralName.replace(/[/\\?%*:|"<>]/g, '-'),
      workSheets: [
        {
          sheetName: this.idea.pluralName,
          startingRowNumber: 1,
          gapBetweenTwoTables: 2,
          tableSettings: {
            data: {
              importable: true,
              headerDefinition: this.idea.properties.map(p => ({
                name: p.name,
                key: p.alias,
                dataType: p.type === 'date' ? 'Date' : p.type === 'number' ? 'NUMBER' : 'String', //NUMBER em maiúscula porque 'Number' sobrescreve o formato.
                width:
                    Math.min(
                        Math.max(
                            p.name.length,
                            ...this.instances.map(i =>
                                p.relationship ? this.$arrays.asArray(this.propValue(i, p.alias)).map((v: any) => v._identifier.length).reduce((s: any, a: any) => s + a, 0) : (p.type === 'number' ? 2 + 1.2 * ('' + i[p.alias]).length : ('' + i[p.alias]).length)
                            ),
                        ) * 1.1
                        , 100)
              }))
            }
          }
        }
      ]
    }
  }

  get excelData() {
    return [
      {
        data: this.instances.map(i => {
          let o: { [k: string]: any } = {}
          this.idea.properties.forEach(p => {
            const vals = this.$arrays.asArray(this.propValue(i, p.alias))
            const text = []
            for (const val of vals) {
              //O que se escolhe por id sai pelo nome — o identificador, a opção, a pessoa
              text.push(p.type === 'date' ? new Date(val as unknown as string) :
                  isChoiceField(p) ? valueText(p, val, this.workspace) : val)
            }
            o[p.alias] = (text.length === 1) ? text[0] : text.join(", ")

          })
          return o
        })
      }
    ]
  }


  //asElement: dentro de v-for o Vue 2 entregava o ref como lista mesmo com nome único,
  //e o Vue 3 entrega o próprio componente
  focusFilter(headerName: string) {
    this.$nextTick(() => {
      const minimumFilter = asElement(this.$refs[`filter-minimum-${headerName}`]) as any
      const maximumFilter = asElement(this.$refs[`filter-maximum-${headerName}`]) as any

      if (maximumFilter && this.filters.maximum[headerName]) {
        maximumFilter.focus()
      } else if (minimumFilter) {
        minimumFilter.focus()
      }

      const textFilter = asElement(this.$refs[`filter-text-${headerName}`]) as any
      if (textFilter) {
        textFilter.focus()
      }
    })

  }

  /**
   * Uma imagem é a URL dela, que muda a cada gravação: não há o que comparar nem o
   * que digitar. Além disso o campo genérico usa :type="property.type", e
   * <input type="image"> é um botão de submit, não um campo de texto.
   */
  isFilterable(property: IdeaProperty) {
    return !isMediaType(property)
  }

  isRangeType(property: IdeaProperty) {
    //seleção e usuário guardam número, mas é id: faixa de ids não quer dizer nada
    if (isChoiceField(property)) return false
    return !['text'].includes(property.type!)
  }

  isFiltered(alias: string) {
    const res = this.filters.minimum[alias] || this.filters.maximum[alias] || this.filters.text[alias] || asArray(this.filters.rel[alias]).length
    return res
  }

  exportToExcel() {
    const excelExport = new ExcelExport()
    excelExport.downloadExcel(this.excelSettings, this.excelData)
  }

  asArray(elements: Array<any>) {
    return asArray(elements)
  }

  /**
   * Compara duas instâncias pela POSIÇÃO da situação no fluxo.
   *
   * A instância guarda o id da situação, que não tem ordem nenhuma: a ordem que significa
   * algo é a do fluxo — a mesma em que as situações aparecem no design e nas faixas do
   * Kanban. É o `positionOf`, o mesmo que a coluna de seleção usa sobre o domínio.
   *
   * Pelo currentStateOf, e não por `_state` direto: instância sem situação gravada
   * pertence ao estado inicial, e é assim que o resto da tela a trata.
   */
  stateSort(a: Instance, b: Instance) {
    const position = (instance: Instance) =>
        this.workflow!.positionOf(this.workflow!.currentStateOf(instance).id)
    return position(a) - position(b)
  }

  /**
   * Compara duas instâncias pelo texto de um relacionamento.
   *
   * Múltiplo vira a lista de nomes na ordem em que está, juntada — é o que a célula
   * mostra. Sem nome (relacionamento vazio, ou _rels ainda não carregado) cai no id,
   * que ao menos é estável.
   *
   * `numeric` porque identificador com número é comum, e sem ele "Item 10" vem antes
   * de "Item 2".
   */
  relationshipSort(alias: string) {
    return this.textSort(instance => asArray(this.propValue(instance, alias))
        .map((value: any) => (value && value._identifier) ?? value)
        .join(', '))
  }

  /** Compara pelo nome das pessoas, como a célula as mostra; múltiplo junta os nomes na ordem. */
  userSort(property: IdeaProperty) {
    return this.textSort(instance => asArray(instance[property.alias])
        .map((value: any) => valueText(property, value, this.workspace))
        .join(', '))
  }

  /** Compara pelo texto que `text` tira de cada instância — o critério dos dois de cima. */
  textSort(text: (instance: Instance) => string) {
    return (a: Instance, b: Instance) =>
        text(a).localeCompare(text(b), undefined, {sensitivity: 'accent', numeric: true})
  }

  /**
   * Compara pela posição da opção no domínio. Múltiplo compara a lista de posições, na
   * ordem do domínio, elemento a elemento; vazio e opção desconhecida vão para o fim.
   */
  choiceSort(property: IdeaProperty) {
    const positions = (instance: Instance) => {
      const domain = domainOfProperty(property, this.workspace.domains)
      if (!domain) return []
      return asArray(instance[property.alias])
          .map((id: any) => domain.positionOf(id))
          .sort((x: number, y: number) => x - y)
    }
    return (a: Instance, b: Instance) => {
      const pa = positions(a), pb = positions(b)
      for (let i = 0; i < Math.max(pa.length, pb.length); i++) {
        const x = pa[i] ?? Number.MAX_SAFE_INTEGER, y = pb[i] ?? Number.MAX_SAFE_INTEGER
        if (x !== y) return x - y
      }
      return 0
    }
  }

  /** As opções do domínio de um atributo de seleção, para o filtro da coluna. */
  choiceOptions(property: IdeaProperty) {
    const domain = domainOfProperty(property, this.workspace.domains)
    return domain ? domain.options : []
  }

  propValue(instance: Instance, alias: string) {
    // if(this.instance._rels && this.instance._rels[alias]) return this.instance._rels[alias]
    // else return this.instance[alias]
    return instance._rels && instance._rels[alias] || instance[alias]
  }

  /**
   * A situação do item como opção colorida. Pode contar com o fluxo: a coluna _state só entra
   * nos cabeçalhos quando ele existe.
   */
  stateOption(instance: Instance): ColoredOption {
    return this.workflow!.coloredStateOf(instance)
  }

  /**
   * Os valores que o filtro de um relacionamento oferece: os presentes entre os itens
   * carregados, menos os terminados quando não se está mostrando terminados.
   *
   * O "presentes, sem repetição" saiu daqui para o `relatedValues` — nunca foi uma regra de
   * listagem, e é a mesma pergunta que a dimensão de relacionamento do Kanban faz. O que ficou
   * é o que é desta tela: esconder do filtro o item que já fechou.
   */
  uniqueRelValues(property: IdeaProperty) {
    const res = relatedValues(this.instances, property.alias)

    if (!this.isShowClosedItems) {
      const idea = this.workspace.ideas.find(idea => idea.alias === property.relationship?.idea)!
      const workflow = this.workspace.workflowOf(idea)
      if (workflow) {
        return res.filter(value =>
            this.filters.rel[property.alias].includes(value._instance) ||
            !workflow.isFinal(value))
      }
    }
    return res;
  }

  isThereWorkflowWithClosedStatesAdvanced(workflow: EnhancedWorkflow, instances: Instance[]) {
    const finalStates = workflow.finalStates
    //Se existem estados closed e se alguma instância está nele, retorna true
    if (finalStates.length &&
        instances.some(instance => workflow.isFinal(instance))
    ) return true
  }


  isThereWorkflowWithClosedStates() {
    //Verificar se a própria idea tem workflow com estados finais sendo utilizados
    if (this.workflow) {
      if (this.isThereWorkflowWithClosedStatesAdvanced(this.workflow, this.instances)) {
        return true;
      }
    }

    //Verificar se a idea de algum relacionamento tem workflow com estados finais sendo utilizados
    return this.idea.properties.filter(p => p.relationship?.idea).some(property => {
      const idea = this.workspace.ideas.find(idea => idea.alias === property.relationship?.idea)!
      if (idea.workflow) {
        const propertyInstances = this.instances.map(instance => asArray(instance._rels[property.alias])).flat()
        const workflow = this.workspace.workflowOf(idea)!
        if (this.isThereWorkflowWithClosedStatesAdvanced(workflow, propertyInstances)) {
          return true;
        }
      }
    })

    // let relProperties = this.idea.properties.filter(p => p.relationship?.idea);
    // const relPropertiesWithWorkflow = relProperties.filter(p => this.workspace.ideas.find(idea => idea.alias === p.relationship?.idea && idea.workflow) )
    // return this.workspace.workflows.some(workflow =>
    //     //WORKFLOWS com final states
    //     workflow.states.some(state => state.final) && relPropertiesWithWorkflow.length
    //     //WORKFLOWS usados em alguma idea de algum relacionamento
    // )


  }

  /** O que o item, como está gravado, viola neste campo — a frase do ⚠ da célula, ou vazio. */
  cellFailures(instance: Instance, property: IdeaProperty): string {
    return failuresAt(instance, property.alias).map(failure => failureText(failure, this.idea)).join(' · ')
  }

  propertyItems(property: IdeaProperty, instance: Instance) {
    const values = asArray(this.propValue(instance, property.alias))
    if (property.relationship) {
      const relIdea = this.ideas.find(idea => idea.alias === property.relationship!.idea)!
      if (relIdea.workflow && !this.isShowClosedItems) {
        const workflow = this.workspace.workflowWith(relIdea.workflow)!
        return values.filter(value => !workflow.isFinal(value))
      }
    }
    return values
  }

  ideaWorkflow(ideaAlias: string) {
    return this.workspace.ideaWith(ideaAlias)?.workflow
  }

}

export default toNative(ListInstances)
</script>
<template>
  <ontos-base v-bind="baseProps" ref="base">
    <div v-if="loaded">
      <ontos-page-header :title="idea.pluralName">
        <!-- Só no xs: acima disso as visões vivem na faixa do caminho, no App.vue -->
        <template v-if="$vuetify.display.xs" #tabs>
          <ontos-collection-view-tabs :idea="idea" :ideas="workspace.ideas"/>
        </template>
        <template #actions>
          <ontos-show-closed-toggle v-if="isThereWorkflowWithClosedStates()" v-model="isShowClosedItems"/>
          <ontos-check-menu icon="mdi-format-columns" :label="$tt('column-config')"
                            :items="columnMenuItems"
                            :model-value="visibleColumns"
                            :min="1"
                            @update:model-value="onColumnsChange"/>
          <!-- Só no xs: acima dele o "Criar" mora na faixa do caminho, que é onde estão as visões da
               coleção (ver o `createIdea` do App). Lá a faixa não existe, e ele volta para cá. -->
          <ontos-header-action v-if="canCreateIn(idea) && $vuetify.display.xs" icon="mdi-plus"
                               :label="$tt('instance-create', {collection: idea.name})"
                               @click="requestCreation(idea.alias)"/>
          <!-- Exportação para excel desabilitada TODO: Migrar para exceljs-->
          <!-- O Design não mora mais aqui: está na faixa do caminho e na barra do app (ver o App) -->
        </template>
      </ontos-page-header>
      <v-row :no-gutters="isThereWorkflowWithClosedStates()">
        <v-col>
          <ontos-table-fit v-slot="{ compact }" :columns-key="visibleColumns.join(',')">
            <!-- Na visão compacta o cabeçalho das colunas some (a lista não tem colunas), e com ele o funil e
                 o clique de ordenar: os dois voltam aqui, em botões (escolha do usuário) -->
            <div v-if="compact && idea.properties.length && instances.length > 1" class="compact-tools">
              <v-btn size="small" variant="tonal" prepend-icon="mdi-filter"
                     :color="showFilter ? 'primary' : undefined" @click="showFilter = !showFilter">
                {{ $tt('filter.text') }}
              </v-btn>
              <v-menu location="bottom start">
                <template #activator="{props: menuProps}">
                  <v-btn v-bind="menuProps" size="small" variant="tonal" prepend-icon="mdi-sort">
                    <template v-if="currentSort">
                      {{ currentSort.title }}
                      <v-icon end size="small">{{ currentSort.order === 'desc' ? 'mdi-arrow-down' : 'mdi-arrow-up' }}</v-icon>
                    </template>
                    <template v-else>{{ $tt('sort') }}</template>
                  </v-btn>
                </template>
                <v-list density="compact" min-width="200">
                  <v-list-item v-for="header of sortableHeaders" :key="header.key" :title="header.title"
                               :active="!!currentSort && sortBy[0].key === header.key" @click="sortOn(header.key)">
                    <template v-if="currentSort && sortBy[0].key === header.key" #append>
                      <v-icon size="small">{{ currentSort.order === 'desc' ? 'mdi-arrow-down' : 'mdi-arrow-up' }}</v-icon>
                    </template>
                  </v-list-item>
                </v-list>
              </v-menu>
            </div>
            <v-data-table
                ref="instancesTable"
                tabindex="-1"
                v-if="idea.properties.length"
                @click:row="(event, {item}) => openRow(event, item)"
                v-model:sort-by="sortBy"
                :hide-default-header="compact"
                :headers="headers"
                :items="instances"
                :items-per-page-options="itemsPerPageOptions"
                :items-per-page="itemsPerPage"
                class="ontos-list-table"
                :row-props="{class: 'ontos-list-row'}"
                item-value="_instance"
                mobile-breakpoint="0"
                hover
            >

              <!--              -->

              <!--@click:row="$router.push('/instances/book/1')"-->

              <!--mobile-breakpoint="0"-->

              <template #[h.slot]="{ column }" v-for="h in headers.slice(0, headers.length)" :key="h.key">
                <span>{{ column.title }}</span
                ><a
                  v-if="isFiltered(column.key) && !showFilter"
                  @click.stop="
                    showFilter = true
                  "
                  :key="`isFiltered-${h.key}`"
              >
                {{ $tt('filter.active') }}
              </a>
                <!--
                <v-btn :key="`filter-${h.value}`" icon @click.stop="filter(header.value)"
                  ><v-icon :class="currentFilter == header.value ? 'text-primary' : ''" size="small">mdi-filter</v-icon></v-btn
                >-->
                <!--
                :class="
                  currentFilter == header.value || isFiltered(header.value)
                    ? 'text-primary'
                    : ''
                "-->
              </template>

              <!-- A mesma régua de pé do cabeçalho da ficha, agora marcando a linha: a coluna
                   do filtro não tem corpo nenhum, então ela recebe o acento sem custar uma
                   coluna nova nem empurrar o conteúdo. -->
              <!-- O nome do item, pelo mesmo componente que as tabelas da ficha usam. -->
              <template #[`item._identifier`]="{ item }">
                <ontos-instance-identifier :instance="item" :idea="idea"/>
              </template>

              <template #[`item._filter`]="{ item }">
                <ontos-color-accent row :color="instanceColorOf(item)"/>
              </template>

              <template #[`header._filter`]="{  }">
                <div class="px-0 mx-0" style="white-space: nowrap;">
                  <v-btn
                      variant="plain"
                      size="small"
                      v-show="!showFilter && instances.length > 1"
                      icon
                      @click="enableFilter(); focusFilter(headers[1].key)"
                  >
                    <v-icon size="small">mdi-filter</v-icon>
                  </v-btn
                  >
                </div>
              </template>

              <template #[`body.prepend`]="{ }">
                <!-- Na visão compacta, as mesmas células empilhadas (ver .compact-filter-row), cada uma com o
                     nome da coluna — que lá não está no cabeçalho para dizer de quem é o filtro. -->
                <tr class="elevation-5" :class="{'compact-filter-row': compact}">
                  <template v-if="showFilter && instances.length > 1">
                    <td v-if="!compact"></td>
                    <!-- A coluna do identificador não filtra: quem filtra são os campos que o
                         compõem, cada um na sua. A célula existe para as colunas não
                         desalinharem. -->
                    <td v-if="hasCompositeIdentifier(idea) && !compact"></td>
                    <td
                        v-for="property in visibleProperties"
                        :key="property.alias"
                        style="vertical-align: top; max-width: 200px"
                        class="pl-3 py-1"
                    >
                      <div v-if="compact && isFilterable(property)"
                           class="compact-filter-label text-caption text-medium-emphasis">{{ property.name }}</div>
                      <!-- imagem não tem por onde filtrar: a célula fica vazia, mas o
                           td continua existindo para as colunas não desalinharem -->
                      <div v-if="isFilterable(property)">
                        <template v-if="isRangeType(property)">
                          <!-- Date Range -->
                          <template v-if="property.type === 'date'">
                            {{ filters.maximum[property.alias] }}
                            <v-text-field
                                class="compact"
                                type="date"
                                density="compact"
                               
                                variant="outlined"
                                clearable
                                hide-details
                                :label="$tt('filter.min')"
                                :counter="property.length"
                                :ref="`filter-minimum-${property.alias}`"
                                v-model="filters.minimum[property.alias]"
                            />
                            <v-text-field
                                class="compact mt-1"
                                type="date"
                                density="compact"
                                clearable
                                variant="outlined"
                                hide-details
                                :label="$tt('filter.max')"
                                :counter="property.length"
                                :ref="`filter-maximum-${property.alias}`"
                                v-model="filters.maximum[property.alias]"
                            />
                          </template>
                          <!-- Not date Range -->
                          <template v-else>
                            <v-text-field
                                class="compact"
                                density="compact"
                                variant="outlined"
                                hide-details
                                :label="$tt('filter.min')"
                                :counter="property.length"
                                :ref="`filter-minimum-${property.alias}`"
                                v-model="filters.minimum[property.alias]"
                            />
                            <v-text-field
                                class="compact mt-1"
                                density="compact"
                                variant="outlined"
                                hide-details
                                :label="$tt('filter.max')"
                                :counter="property.length"
                                :ref="`filter-maximum-${property.alias}`"
                                v-model="filters.maximum[property.alias]"
                            />
                          </template>


                        </template>
                        <!-- O mesmo seletor do formulário, múltiplo: filtra pelas opções
                             marcadas, e não por texto digitado ou por faixa. -->
                        <ontos-choice-input v-else-if="property.type === 'choice'"
                                            class="compact"
                                            :value="filters.rel[property.alias]" @input="filters.rel[property.alias] = $event"
                                            :options="choiceOptions(property)"
                                            :label="property.name"
                                            hide-label
                                            outlined
                                            dense
                                            multiple/>
                        <!-- O mesmo seletor de pessoas do formulário, múltiplo: filtra pelas pessoas
                             marcadas. Todas as do ambiente, e não só as escolhíveis: quem saiu
                             continua nos itens, e filtrar por ele faz sentido. -->
                        <ontos-person-select v-else-if="property.type === 'user'"
                                             class="compact"
                                             :model-value="filters.rel[property.alias]"
                                             @update:model-value="filters.rel[property.alias] = $event"
                                             :people="workspace.people"
                                             multiple
                                             density="compact"
                                             variant="outlined"
                                             hide-details/>
                        <template v-else-if="property.relationship">
                          <ontos-instance-rel-input
                              v-if="uniqueRelValues(property).length > 1"
                              class="compact"
                              :value="filters.rel[property.alias]" @input="filters.rel[property.alias] = $event"
                              :items="uniqueRelValues(property)"
                              :property="property"
                              hide-label
                              outlined
                              dense
                              force-multiple
                          />
                        </template>
                        <v-text-field
                            v-else
                            class="compact"
                            density="compact"
                            variant="outlined"
                            hide-details
                            :type="property.type"
                            :label="$tt('filter.text')"
                            :counter="property.length"
                            :ref="`filter-text-${property.alias}`"
                            v-model="filters.text[property.alias]"
                        />
                      </div>
                    </td>
                    <td style="width: 10px" v-if="idea.workflow">
                      <div v-if="compact" class="compact-filter-label text-caption text-medium-emphasis">
                        {{ $tt('state') }}
                      </div>
                      <ontos-instance-state-input :value="filters.rel._state" @input="filters.rel._state = $event" multiple :workflow="workflow" dense
                                                  outlined hide-label class="compact"/>
                    </td>
                  </template>

                  <!-- Na visão compacta quem fecha é o próprio botão "Filtrar", no alto -->
                  <td v-if="showFilter && !compact" class="px-0" style="vertical-align: top; text-align: right">
                    <v-btn variant="plain" icon @click="showFilter = false">
                      <v-icon>mdi-close</v-icon>
                    </v-btn>
                  </td>
                </tr>
              </template>
<!-- Sem slot de corpo para a primeira coluna: ela existe só para hospedar o
                   botão de filtro no cabeçalho. Visualizar é o clique na linha, e
                   editar e excluir ficam na ficha que ele abre. -->
              <template #[`item.${property.alias}`]="{ item }"
                        v-for="property in idea.properties">
                <!-- A calculada passa pelo mesmo desenho dos outros campos: o valor chega pronto do
                     servidor, na forma gravada, e por isso uma seleção calculada vira chip e uma
                     data calculada vem formatada. Ela tinha um ramo próprio, que mostrava o valor
                     cru — o id da opção — e um "calculando" enquanto o navegador a executava. -->
                <template v-if="fieldVisible(item, property)">
                    <v-progress-circular
                        v-if="property.relationship && !asArray(propValue(item, property.alias)).every(v => v._identifier)"
                        :key="property.alias" color="teal" indeterminate/>
                    <template v-else v-for="(value, index) in propertyItems(property, item)" :key="property.alias + '.' + index">
                      <!-- Quem já se delimita não leva barra: a mídia, cujos controles ficam
                           lado a lado e cujo separador estouraria a largura da coluna; o
                           relacionamento e a seleção, que viram chip. Ver needsValueSeparator. -->
                      <template v-if="index > 0 && needsValueSeparator(property)"
                      ><span class="pr-1">&puncsp;|&puncsp;</span>
                      </template>
                      <ontos-property-value
                          :property="property"
                          :value="value"
                          :item="item"
                          :context="{...$route.params, instance: item._instance}"
                          :choice-domains="workspace.domains"
                          variant="cell"
                          @preview="imagePreview = $event"
                          @link="onInstanceLink($event.event, $event.context)"/>
                    </template>
                    <ontos-hidden-count :key="`${property.alias}.hidden`" :count="hiddenCount(item, property)"/>
                    <!-- O que o item, como está gravado, viola neste campo: um ⚠ com a frase ao
                         passar o mouse — aviso, e não erro; ninguém é barrado por ele -->
                    <v-icon v-if="cellFailures(item, property)" :key="`${property.alias}.failures`"
                            size="x-small" color="warning" class="ml-1"
                            :title="cellFailures(item, property)">mdi-alert-outline</v-icon>
                </template>
              </template>
              <!-- Na visão compacta, o item inteiro numa célula só, como a linha de ambiente da tela inicial
                   (escolha do usuário) — ver o InstanceListRow. O slot da linha substitui a do Vuetify,
                   e com ela o `click:row`: o clique volta aqui. -->
              <template v-if="compact" #item="{ item, columns }">
                <tr class="compact-list-item" @click="openRow($event, item)">
                  <td :colspan="columns.length" class="pa-0">
                    <ontos-instance-list-row :instance="item" :idea="idea" :fields="rowFields(item)"
                                             :state="workflow ? stateOption(item) : null"
                                             :context="{...$route.params, instance: item._instance}"
                                             :choice-domains="workspace.domains"/>
                  </td>
                </tr>
              </template>
              <!-- A bolinha e o nome saem do mesmo componente que desenha uma opção de
                   seleção e uma raia do Kanban: é a mesma coisa vista na mesma forma. A situação é
                   sempre o semáforo com o nome ao lado — no chip ela quebrava o padrão do app
                   (relato do usuário). -->
              <template #[`item._state`]="{ item }">
                <ontos-colored-option :option="stateOption(item)" dot/>
              </template>
            </v-data-table>
            <!--:to="{name: "view-instance", params: {...$route.params, idea: idea[propertyName].idea, instance: item[propertyName]}}"-->
            <!--            <editor v-model="texto" initialEditType="wysiwyg" />-->
          </ontos-table-fit>
        </v-col>
      </v-row>
      <!--      <v-row justify="end" class="mt-3">-->
      <!--        <v-col cols="auto">-->
      <!--          <v-btn @click="exportToExcel()"><v-icon start>mdi-microsoft-excel</v-icon> Export to excel</v-btn>-->
      <!--        </v-col>-->
      <!--      </v-row>-->
    </div>
    <!-- Editar e excluir vivem na ficha, que o clique na linha abre.

         Aqui os dois recarregam a tabela, e não só a linha: ela não tem foto de raias a
         preservar nem rolagem que custe, e a releitura é a mesma que a visão irmã aproveita.
         Quem precisa de cirurgia é o quadro. -->
    <ontos-view-instance-dialog v-if="instanceOnView"
                                :context="instanceOnView"
                                :preview="previewOnView"
                                :workspace="workspace"
                                @changed="load()"
                                @deleted="load()"
                                @closed="instanceOnView = null"
                                @showMessage="$emit('showMessage', $event)"
                                @showErrorMessage="$emit('showErrorMessage', $event)"/>

    <!-- Um por tela, não um por célula: guardado aqui, o que está ampliado não muda
         quando a tabela é reordenada ou filtrada -->
    <ontos-image-preview-dialog v-if="imagePreview"
                                :src="imagePreview.src"
                                :title="imagePreview.title"
                                @closed="imagePreview = null"/>

  </ontos-base>
</template>

<style scoped>
/* Célula com 5px de recuo lateral e cabeçalho numa linha só. No Vuetify 2 o recuo vinha
   de uma variável CSS privada da tabela (--v-data-table-padding), que o 4 não tem; aqui é
   regra comum. :deep() porque th e td são desenhados pela v-data-table, não por este
   template — só a raiz dela recebe o escopo. */
.v-data-table :deep(th),
.v-data-table :deep(td) {
  padding: 0 5px;
}

.v-data-table :deep(th) {
  white-space: nowrap;
}

.v-data-table :deep(tbody tr > td) {
  cursor: pointer;
}

/* A célula vira referência de posição para a régua: o resto da receita mora no modo `row`
   do próprio ColorAccent, porque a mesma marca existe também nas tabelas da ficha. */
.v-data-table :deep(tbody td:first-child) {
  position: relative;
}

.compact {
  transform: scale(0.875);
  transform-origin: left;
  max-width: 115%;
  width: 115%;
}

@media (max-width: 600px) {

  .v-data-table :deep(th),
  .v-data-table :deep(td) {
    padding: 0px 3px !important;
  }
}

/* O Filtrar e o Ordenar da visão compacta, acima da lista */
.compact-tools {
  display: flex;
  gap: 8px;
  margin-bottom: 8px;
}

/* As células do filtro uma embaixo da outra, na largura toda: lado a lado, como colunas, não
   cabiam na visão compacta */
.compact-filter-row,
.compact-filter-row > td {
  display: block;
  width: 100%;
  max-width: none !important;
}

.compact-filter-row > td {
  padding: 4px 0 !important;
}

.compact-filter-label {
  margin-bottom: 2px;
}

.compact-list-item:hover {
  background-color: rgba(var(--v-theme-on-surface), 0.04);
}

/* .url-value mudou-se para o PropertyValue.vue junto com a marcação que a usava. O markdown da
   célula também é dele: o excerto em duas linhas e o ⤢, no lugar da caixa que crescia. */
/*
:deep(.toastui-editor-contents *) {
  margin: 0.2em;
}
*/
</style>
